import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { AdmitService, PoolLiteTO } from "./admit.service";
import { Integration, IntegrationService, IntegrationTypes } from "../integrations/integration.service";
import { S25Util } from "../../util/s25-util";
import { EventService } from "../../services/event.service";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { Bind } from "../../decorators/bind.decorator";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";

@TypeManagerDecorator("s25-ng-admit-action-link")
@Component({
    selector: "s25-ng-admit-action-link",
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (init) {
            @if (showLink) {
                <ng-container *ngTemplateOutlet="admitLink"></ng-container>
            } @else {
                @if (preAction === "createPool") {
                    <button
                        (click)="initModal(admitCreatePoolModal)"
                        class="ngInlineBlock ngOptElementPad c-textButton"
                    >
                        <s25-ng-icon [type]="'contact'" [ariaLabel]="false" />
                        {{ linkText }}
                    </button>
                } @else {
                    <ng-container *ngTemplateOutlet="actionButtonOrStatus"></ng-container>
                }
            }

            <s25-ng-modal #admitCreatePoolModal [title]="linkText">
                <ng-template #s25ModalBody>
                    <div class="c-margin-bottom--single">Manage registrants in your own CRM</div>
                    <label class="ngBlock c-margin-bottom--single">
                        Pool Name (Required):
                        <input class="c-input" type="text" [(ngModel)]="poolName" />
                    </label>
                    <label class="ngBlock c-margin-bottom--single">
                        Select a Parent Pool (Optional):
                        <select class="cn-form__control" [(ngModel)]="parentPoolId">
                            @for (pool of pools; track pool.id) {
                                <option [ngValue]="pool.id">{{ pool.name }}</option>
                            }
                        </select>
                    </label>

                    <ng-container *ngTemplateOutlet="actionButtonOrStatus"></ng-container>
                </ng-template>
                <ng-template #s25ModalFooter>
                    <div class="buttons">
                        <button class="aw-button aw-button--outline cancel" (click)="onModalCancel()">Close</button>
                    </div>
                </ng-template>
            </s25-ng-modal>

            <ng-template #actionButtonOrStatus>
                @if (!integration && preActionProcessing) {
                    <div>
                        Creating your new CRM instance! This may take a few minutes. When everything is ready, a link to
                        the CRM will appear here.
                    </div>
                } @else if (showLink) {
                    <div>Your pool has been created! Click the link below to manage your attendees.</div>
                    <ng-container *ngTemplateOutlet="admitLink"></ng-container>
                } @else {
                    <s25-ng-button [type]="'outline'" [onClick]="runPreAction">{{ preActionName }}</s25-ng-button>
                }
            </ng-template>

            <ng-template #admitLink>
                <a
                    class="ngInlineBlock ngOptElementPad"
                    href="{{ redirectLink }}"
                    [target]="target"
                    data-nav-link="crm"
                    role="option"
                >
                    <s25-ng-icon [type]="'contact'" [ariaLabel]="false" />
                    {{ linkText }}
                </a>
            </ng-template>
        }
    `,
    standalone: false,
    encapsulation: ViewEncapsulation.Emulated,
    styles: [
        `
            :host {
                label,
                div,
                input,
                select,
                span {
                    color: rgba(0, 0, 0, 0.8) !important;
                }
            }

            :host-context(body.nm-party--on) {
                label,
                div,
                input,
                select,
                span {
                    color: rgba(255, 255, 255, 0.8) !important;
                }
            }
        `,
    ],
})
export class AdmitActionLinkComponent implements OnInit {
    @ViewChild(S25ModalComponent) modal: S25ModalComponent;

    constructor(private cd: ChangeDetectorRef) {}

    @Input({ required: true }) preAction: "createInstance" | "createPool" | "none";
    @Input() eventId?: number;

    init = false;
    showLink = false;
    preActionProcessing = false;
    eventAlreadyLinked = false;
    parentPoolId: number;
    integration: Integration;
    pools: PoolLiteTO[] = [];
    poolName = "";
    preActionName = "";
    redirectLink = "";
    target = "_blank";
    linkText = "";

    runPreAction = async () => {
        if (this.preAction === "createPool" && !this.poolName) {
            alert("Please enter a pool name.");
            return;
        }

        try {
            this.preActionProcessing = true;
            this.cd.detectChanges();
            if (!this.integration) {
                this.integration = await AdmitService.createIntegration();
            }
            if (this.preAction === "createPool" && this.eventId && this.poolName) {
                const evCustAtrb = await AdmitService.createPool(this.eventId, this.poolName, this.parentPoolId);
                const poolId = evCustAtrb.custAtrbNVal;
                this.eventAlreadyLinked = true;
                this.redirectLink = AdmitService.getAdmitEventsLink(poolId);
            }
            this.showLink = true;
            this.preActionProcessing = false;
            this.cd.detectChanges();
        } catch (ex) {
            this.preActionProcessing = false;
            this.showLink = false;
            this.cd.detectChanges();
            S25Util.showError(ex);
        }
    };

    @Bind
    async onModalCancel() {
        await this.modal.close();
    }

    @Bind
    async initModal(modal: S25ModalComponent) {
        return modal.open();
    }

    async ngOnInit() {
        this.integration = await IntegrationService.getIntegration(IntegrationTypes.admit);
        this.redirectLink = AdmitService.getRedirectUrl();
        if (["createInstance", "none"].indexOf(this.preAction) > -1) {
            this.linkText = "Go to CRM";
            this.preActionName = "Create CRM Instance";
        } else if (this.preAction === "createPool") {
            this.linkText = "Manage Attendees";
            this.preActionName = "Create CRM Pool";
            this.target = "_blank";

            const event = await EventService.getEventInclude(this.eventId, ["attributes"]);
            if (event?.event_name) {
                this.poolName = event.event_name;
            }
            const poolIdStr = (event?.custom_attribute?.filter(
                (attr) => attr.attribute_id === CustomAttributes.system.AdmitPoolId.id,
            ) ?? [])[0]?.attribute_value;

            if (poolIdStr) {
                const poolId = parseInt(poolIdStr);
                this.eventAlreadyLinked = true;
                this.redirectLink = AdmitService.getAdmitEventsLink(poolId);
            } else {
                this.eventAlreadyLinked = false;
            }

            if (this.integration && !this.eventAlreadyLinked) {
                const [pools, error] = await S25Util.Maybe(AdmitService.getPools());
                if (error) {
                    S25Util.showError(error);
                } else {
                    this.pools = pools;
                    this.pools.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
                }
            }
        }

        // only show the link if the integration is enabled and the pre-action is to create an instance
        // or if the event is already linked to a pool
        // bc if they are creating a pool, the user must name the pool and optionally choose a parent pool first
        this.showLink =
            !!this.integration && (["createInstance", "none"].indexOf(this.preAction) > -1 || this.eventAlreadyLinked);

        // if the link is not shown (no integration) and there's no pre-action, don't show anything
        if (!this.showLink && this.preAction === "none") {
            return;
        }

        this.init = true;
        this.cd.detectChanges();
    }
}
